<template>
  <div class="omx-radiogroup" :class="{ 'omx-radiogroup--view-only': viewOnly, ['omx-radiogroup--' + direction]: true }">
    <label v-if="label" class="omx-radiogroup__label">{{ label }}</label>

    <div v-if="viewOnly" class="omx-radiogroup__value">
      <slot name="value">{{ selectedLabel || '—' }}</slot>
    </div>

    <div v-else class="omx-radiogroup__options">
      <label
          v-for="opt in normalizedOptions"
          :key="String(opt.value)"
          class="omx-radiogroup__option"
          :class="{ 'is-selected': isSelected(opt), 'is-disabled': opt.disabled }"
      >
        <input
            type="radio"
            class="omx-radiogroup__input"
            :name="groupName"
            :value="opt.value"
            :checked="isSelected(opt)"
            :disabled="opt.disabled"
            :tabindex="tabindex"
            @change="onChange(opt)"
        />
        <span class="omx-radiogroup__dot" aria-hidden="true"></span>
        <span class="omx-radiogroup__text">{{ opt.label }}</span>
      </label>
    </div>
  </div>
</template>

<script>
/**
 * OmxRadioGroup — radio-button group in the Omx visual language.
 *
 *   <OmxRadioGroup
 *       v-model="personalData.group"
 *       :label="$t('personal-details-view.personal-group-field')"
 *       :options="[
 *         { value: 'internal', label: $t('enums.personal-group.internal') },
 *         { value: 'external', label: $t('enums.personal-group.external') }
 *       ]"
 *       :view-only="!isEditMode"
 *   />
 *
 * View mode renders the selected option's label as a plain text value
 * (use the `#value` named slot to override). Edit mode renders one
 * native radio per option, styled as a pill row.
 *
 * Direction can be set to 'horizontal' (default) or 'vertical'.
 */

let nextId = 0;

export default {
  name: "OmxRadioGroup",
  props: {
    value:     { type: [String, Number, Boolean], default: null },
    label:     { type: String, default: null },
    options:   { type: Array, default: () => [] },
    viewOnly:  { type: Boolean, default: false },
    direction: { type: String, default: "horizontal" },
    tabindex:  { type: [Number, String], default: null }
  },
  data() {
    return { groupName: "omx-radio-" + (++nextId) };
  },
  computed: {
    normalizedOptions() {
      return (this.options || []).map(o => {
        if (o == null) return { value: null, label: "" };
        if (typeof o === "string" || typeof o === "number") return { value: o, label: String(o) };
        return { value: o.value, label: o.label, disabled: !!o.disabled };
      });
    },
    selectedLabel() {
      const match = this.normalizedOptions.find(o => o.value === this.value);
      return match ? match.label : null;
    }
  },
  methods: {
    isSelected(opt) {
      return opt.value === this.value;
    },
    onChange(opt) {
      if (opt.disabled) return;
      this.$emit("input", opt.value);
      this.$emit("change", opt.value);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-radiogroup {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}
.omx-radiogroup__label {
  font-size: 12.5px;
  font-weight: 600;
  color: $omx-ink-700;
}
.omx-radiogroup__value {
  font-size: 14px;
  color: $omx-ink-900;
  min-height: 32px;
  display: flex;
  align-items: center;
}

.omx-radiogroup__options {
  display: flex;
  gap: 8px;
}
.omx-radiogroup--vertical .omx-radiogroup__options {
  flex-direction: column;
  align-items: flex-start;
}

.omx-radiogroup__option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid $omx-ink-200;
  background: #fff;
  border-radius: $omx-radius-md;
  cursor: pointer;
  transition: border-color .12s, background .12s, box-shadow .12s;
  user-select: none;

  &:hover { border-color: $omx-brand-500; }

  &.is-selected {
    border-color: $omx-brand-600;
    background: $omx-brand-50;
    box-shadow: 0 0 0 1px $omx-brand-600 inset;
  }

  &.is-disabled {
    opacity: .55;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.omx-radiogroup__input {
  // Hide the native input but keep it interactive for a11y/keyboard.
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
}

.omx-radiogroup__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1.5px solid $omx-ink-300;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  transition: border-color .12s;
}
.omx-radiogroup__option.is-selected .omx-radiogroup__dot {
  border-color: $omx-brand-600;
  &::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: $omx-brand-600;
  }
}

.omx-radiogroup__text {
  font-size: 14px;
  color: $omx-ink-900;
}
</style>
